import React from 'react';
import { useTranslation } from 'react-i18next';
import ThirtyDayBanner from '../../assets/product/30days.png';
import NinetyDayBanner from '../../assets/product/90days.png';
import ProductImage from '../../assets/product/tryonce.webp';
import TickIcon from '../../assets/black-tick.png';
import PdpTestimonials from '../Customer/components/PdpTestimonials';
import PurchaseCardDetails from '../Customer/components/PurchaseCardDetails';
import { CLASSMED_NINETY_DAY_DISCOUNT_PERCENT } from '../../utils/classmed-pricing';

export const formatClassmedPrice = (amount) => new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}).format(Number(amount || 0));

const PlanIndicator = ({ isSelected }) => (
  <span
    className={`t-mt-[3px] t-flex t-h-5 t-w-5 t-flex-none t-items-center t-justify-center t-rounded-full t-border-2 t-transition-all t-duration-200 ${
      isSelected ? 't-border-white t-bg-white t-p-[3px]' : 't-border-g300 t-bg-transparent'
    }`}
    aria-hidden="true"
  >
    {isSelected && <span className="t-h-full t-w-full t-rounded-full t-bg-navy" />}
  </span>
);

const DetailItem = ({ children }) => (
  <div className="t-flex t-items-center t-gap-2">
    <img src={TickIcon} alt="" className="t-h-4 t-w-4 t-flex-none" />
    <p className="!t-m-0 t-font-sans t-text-[13px] t-text-navy/80">{children}</p>
  </div>
);

const PlanOption = ({ duration, price, fullNinetyDayPrice, isSelected, onSelect }) => {
  const { t } = useTranslation();
  const totalPrice = Number(price || 0);
  const monthlyPrice = duration === 90 ? totalPrice / 3 : totalPrice;
  const dailyPrice = totalPrice / duration;

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      onClick={() => onSelect(duration)}
      className={`t-mb-3 t-w-full t-overflow-hidden t-rounded-xl t-border-2 t-bg-white t-p-0 t-text-left t-transition-all t-duration-150 ${
        isSelected ? 't-border-navy' : 't-border-g200'
      }`}
    >
      <span
        className={`t-flex t-items-center t-justify-between t-gap-3 t-px-3 t-py-3 ${
          isSelected ? 't-bg-navy t-text-white' : 't-bg-g100 t-text-navy'
        }`}
      >
        <span className="t-flex t-items-start t-gap-2">
          <PlanIndicator isSelected={isSelected} />
          <span
            className={`t-font-sans t-text-[15px] ${
              isSelected ? 't-font-bold t-text-white' : 't-font-semibold t-text-navy'
            }`}
          >
            {duration === 30 ? t('Classmed_ThirtyDays') : t('Classmed_NinetyDays')}
            {duration === 90 && (
              <span className="t-ml-2 t-inline-flex t-rounded t-bg-butter t-px-1.5 t-py-0.5 t-text-[11px] t-font-semibold t-text-navy">
                {t('Classmed_SavePercent', {
                  percent: CLASSMED_NINETY_DAY_DISCOUNT_PERCENT,
                })}
              </span>
            )}
          </span>
        </span>
        <span className="t-flex t-flex-col t-items-end">
          <strong
            className={`t-font-sans t-text-xl ${isSelected ? 't-text-white' : 't-text-navy'}`}
          >
            {duration === 90 && fullNinetyDayPrice != null && (
              <span className="t-mr-1 t-text-base t-font-normal t-opacity-50 t-line-through">
                {formatClassmedPrice(fullNinetyDayPrice)}
              </span>
            )}
            {price == null ? '...' : formatClassmedPrice(price)}
          </strong>
          {price != null && (
            <span
              className={`t-font-sans t-text-[13px] ${
                isSelected ? 't-text-white/70' : 't-text-navy/60'
              }`}
            >
              {formatClassmedPrice(monthlyPrice)} {t('Classmed_PerMonth')}
            </span>
          )}
        </span>
      </span>

      {isSelected && price != null && (
        <span className="t-flex t-items-center t-justify-between t-gap-3 t-p-2">
          <span className="t-flex t-flex-col t-gap-2">
            <DetailItem>
              {duration} {t('Classmed_DailyServings')}
            </DetailItem>
            <DetailItem>{t('Classmed_SinglePurchase')}</DetailItem>
          </span>
          <span className="t-flex t-flex-none t-flex-col t-items-center t-justify-center t-rounded-lg t-bg-butter t-px-3.5 t-py-2 t-text-center t-text-navy">
            <strong className="t-font-sans t-text-[18px] t-leading-none">
              {formatClassmedPrice(dailyPrice)}
            </strong>
            <span className="t-mt-1 t-font-sans t-text-[10px] t-font-medium t-leading-none t-opacity-70">
              {t('Classmed_PerDay')}
            </span>
          </span>
        </span>
      )}
    </button>
  );
};

const ClassmedOneOffPurchaseCard = ({
  prices,
  selectedDuration,
  onSelectDuration,
  onCheckout,
  isLoading,
  isDisabled,
  focusArea = [],
  ingredients = [],
}) => {
  const { t } = useTranslation();
  const selectedPrice = prices?.[selectedDuration];
  const fullNinetyDayPrice = prices?.[30] == null ? null : Number(prices[30]) * 3;
  const bannerImage = selectedDuration === 90 ? NinetyDayBanner : ThirtyDayBanner;

  return (
    <section className="t-relative t-flex !t-w-full t-flex-col t-gap-2 t-rounded-lg t-bg-surface t-pb-4 t-pt-2 md:t-pb-6">
      <div className="t-flex t-flex-col t-items-start">
        <h3 className="!t-m-0 t-font-serif t-text-2xl t-font-normal t-text-navy">
          {t('Classmed_SelectDuration')}
        </h3>
        <div className="t-flex t-w-full t-items-end t-justify-between t-gap-2">
          <img
            src={bannerImage}
            alt=""
            className="t-h-[73px] t-mix-blend-multiply"
          />
          <img
            src={ProductImage}
            alt=""
            className="!t-h-[155px] !t-w-[155px] t-object-contain t-mix-blend-multiply"
          />
        </div>
      </div>

      <div className="t-flex t-flex-col" role="radiogroup">
        {[30, 90].map((duration) => (
          <PlanOption
            key={duration}
            duration={duration}
            price={prices?.[duration]}
            fullNinetyDayPrice={fullNinetyDayPrice}
            isSelected={selectedDuration === duration}
            onSelect={onSelectDuration}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={onCheckout}
        disabled={isDisabled || isLoading}
        className="t-w-full t-rounded-full t-border-0 t-bg-quaternary t-px-6 t-py-[15px] t-font-sans t-text-[16px] t-font-bold t-uppercase t-text-white t-transition-colors hover:t-bg-[#2da898] disabled:t-cursor-not-allowed disabled:t-opacity-30"
      >
        {isLoading
          ? t('Classmed_CheckoutLoading')
          : `${t('Classmed_CheckoutButton')} - ${
            selectedPrice == null ? '...' : formatClassmedPrice(selectedPrice)
          }`}
      </button>

      <PurchaseCardDetails
        duration={`${selectedDuration} days`}
        focusArea={focusArea}
        totalIngredients={ingredients.length}
        ingredients={ingredients}
        perDayPriceLabel={
          selectedPrice == null
            ? '...'
            : formatClassmedPrice(Number(selectedPrice) / selectedDuration)
        }
        testimonialsSlot={<PdpTestimonials titleClassName="t-text-left t-text-2xl" />}
        proofTitleClassName="t-text-left t-text-2xl"
        showShippingTrust={false}
        showSavingsSummary={false}
        showScientificName={false}
        showCancelAnytime={false}
        availabilityContent={(
          <>
            <span className="t-font-semibold t-text-green-500">{t('Available')}</span>
            {' - '}
            <span className="t-font-semibold t-text-navy">
              {t('Classmed_PharmacyFulfillment')}
            </span>
          </>
        )}
      />
    </section>
  );
};

export default ClassmedOneOffPurchaseCard;
